import React from "react";
import L from "leaflet";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import { C } from "../theme";
import ProfileAvatar from "../components/ProfileAvatar";
import { getProfilePhotoUrl } from "../services/profilePhoto";
import Card from "../components/Card";
import CollapsibleSection from "../components/CollapsibleSection";
import Badge from "../components/Badge";
import Button from "../components/Button";
import { db } from "../firebase";
import { collection, doc, onSnapshot, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useSessionContext } from "../context/SessionContext";
import { authedWrite } from "../utils/authedWrite";
import { requestSessionAnalysis, ProRequiredError } from "../services/aiClient";
import { isProActive } from "../utils/featureGate";
import { useAnglerCatalog } from "../data/anglerCatalog";
import { resolveVideoUrl } from "../services/r2Video";
import { uploadVideoToFacebookPage } from "../services/facebookUploadClient";
import { publishCatchToYouTube } from "../services/youtubeShare";
import SessionConditionsChart from "../components/SessionConditionsChart";

function formatLength(lengthIn) {
  return typeof lengthIn === "number" ? `${lengthIn.toFixed(1).replace(/\.0$/, "")}"` : "—";
}

function formatWeight(weightLbs) {
  return typeof weightLbs === "number" ? `${weightLbs.toFixed(1).replace(/\.0$/, "")} lbs` : "—";
}

// Match a stored catch species (usually the catalog label, sometimes free-text
// from voice capture) to its catalog entry so we can read that species'
// "quality fish" thresholds. Tolerant: exact id/label, then either-direction
// substring on the leading word.
function findSpeciesBaseline(speciesCatalog, speciesName) {
  if (!Array.isArray(speciesCatalog) || !speciesName) return null;
  const norm = (v) => String(v || "").trim().toLowerCase();
  const target = norm(speciesName);
  if (!target) return null;
  let match =
    speciesCatalog.find((s) => norm(s.id) === target || norm(s.label) === target) || null;
  if (!match) {
    const head = target.split(/[\s(]/)[0];
    match =
      speciesCatalog.find((s) => {
        const label = norm(s.label);
        return label.includes(target) || target.includes(label) || (head && label.includes(head));
      }) || null;
  }
  return match;
}

function formatBaro(barometric) {
  const inHg = barometric?.inHg;
  const trend = barometric?.trend;
  if (typeof inHg === "number" && trend) return `${inHg.toFixed(2)} ${trend}`;
  if (typeof inHg === "number") return inHg.toFixed(2);
  if (trend) return trend;
  return "Baro —";
}

function formatHours(startedAt, endedAt) {
  const started = startedAt?.toDate?.();
  const ended = endedAt?.toDate?.();
  if (!started || !ended) return "0h 00m";
  const diffMs = Math.max(0, ended.getTime() - started.getTime());
  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

function formatDateHeading(startedAt) {
  const started = startedAt?.toDate?.();
  if (!started) return "Session Report";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(started);
}

function toMs(value) {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return 0;
}

function haversineMiles(a, b) {
  if (!a || !b) return 0;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return 3958.7613 * c;
}

function formatMiles(value) {
  if (!Number.isFinite(value) || value <= 0) return "0 mi tracked";
  return `${value.toFixed(1).replace(/\.0$/, "")} mi tracked`;
}

function VideoThumbnail({ src }) {
  const videoRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const handleMeta = () => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0.01;
  };

  const handleSeeked = () => setReady(true);
  const handleError = () => setFailed(true);

  return (
    <div style={{
      width: 72,
      height: 54,
      borderRadius: 7,
      overflow: "hidden",
      flexShrink: 0,
      background: "#0a1929",
      border: `1px solid ${C.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      {failed ? (
        <span style={{ fontSize: 18, opacity: 0.4 }}>🎬</span>
      ) : (
        <video
          ref={videoRef}
          src={src}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={handleMeta}
          onSeeked={handleSeeked}
          onError={handleError}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: ready ? "block" : "none",
          }}
        />
      )}
      {!ready && !failed && (
        <span style={{ fontSize: 18, opacity: 0.4, position: "absolute" }}>🎬</span>
      )}
    </div>
  );
}

const SEQUENCE_COLORS = [C.accent, C.seafoam, C.gold, C.warning, C.teal];

function getSequenceColor(item, options = {}) {
  const { row = false } = options;
  // Missed fish (the one that got away) always read in the warning/orange hue
  // so they're visually distinct from landed catches on the map and log.
  if (item.outcome === "missed") return C.warning;
  const hasGps = Boolean(
    (item.videoGps && typeof item.videoGps.lat === "number" && typeof item.videoGps.lng === "number") ||
    (item.catchGps && typeof item.catchGps.lat === "number" && typeof item.catchGps.lng === "number")
  );

  if (!hasGps) return C.red;
  if (row && !item.certified) return C.red;

  const seed = String(item.id || item.sequence || "1")
    .split("")
    .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return SEQUENCE_COLORS[seed % SEQUENCE_COLORS.length];
}

function sequenceBadgeStyle(color, compact = false) {
  return {
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: color,
    border: "1px solid #ffffff",
    color: C.dark,
    fontSize: 8,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 0 10px ${color}88`,
    flexShrink: 0,
  };
}

function SequenceBadge({ sequence, color, compact = false }) {
  return <span style={sequenceBadgeStyle(color, compact)}>{sequence}</span>;
}

function buildSequenceIcon(sequence, color, missed = false) {
  // Landed catches use a solid filled dot; missed fish use a hollow ring so
  // the two outcomes stay legible even at map zoom.
  const html = missed
    ? `<div style="width:16px;height:16px;border-radius:50%;background:transparent;border:2px solid ${color};color:${color};font-size:8px;font-weight:800;display:flex;align-items:center;justify-content:center;box-shadow:0 0 10px ${color}88;">${sequence}</div>`
    : `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:1px solid #ffffff;color:#05101f;font-size:8px;font-weight:800;display:flex;align-items:center;justify-content:center;box-shadow:0 0 10px ${color}88;">${sequence}</div>`;
  return L.divIcon({
    className: "",
    html,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

export default function ReportScreen({ sessionId: sessionIdProp, highlightCatchId, onHighlightConsumed, userProfile, onBack }) {
  const { userId, sessionId: activeSessionId, lastEndedSessionId } = useSessionContext();
  // Share-later: post already-certified clips to THE 99's Facebook/YouTube from
  // the Trip Report. Gated by the shareVideos preference (same as VideoScreen).
  const [shareVideos, setShareVideos] = React.useState(false);
  const [shareState, setShareState] = React.useState({});

  React.useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    getDoc(doc(db, "userPreferences", userId))
      .then((snap) => {
        if (!cancelled && snap.exists()) setShareVideos(Boolean(snap.data()?.shareVideos));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleShareFacebook = React.useCallback(async (c) => {
    const certId = c.certificateId;
    if (!certId) return;
    setShareState((s) => ({ ...s, [certId]: { ...s[certId], fb: "posting", fbErr: "" } }));
    try {
      const title = `${c.species || "Catch"} - ${c.size || ""} ${c.weight || ""}`.trim();
      const description = [
        "Uploaded from The 99",
        c.lure && c.lure !== "\u2014" ? `Lure: ${c.lure}` : "",
      ]
        .filter(Boolean)
        .join(" | ");
      const res = await uploadVideoToFacebookPage({ certId, title, description });
      setShareState((s) => ({ ...s, [certId]: { ...s[certId], fb: "confirmed", fbUrl: res?.permalinkUrl || "" } }));
    } catch (err) {
      setShareState((s) => ({ ...s, [certId]: { ...s[certId], fb: "failed", fbErr: err?.message || "Facebook share failed." } }));
    }
  }, []);

  const handleShareYouTube = React.useCallback(async (c) => {
    const certId = c.certificateId;
    if (!certId) return;
    setShareState((s) => ({ ...s, [certId]: { ...s[certId], yt: "posting", ytErr: "" } }));
    try {
      const res = await publishCatchToYouTube(certId);
      setShareState((s) => ({ ...s, [certId]: { ...s[certId], yt: "confirmed", ytUrl: res?.url || "" } }));
    } catch (err) {
      setShareState((s) => ({ ...s, [certId]: { ...s[certId], yt: "failed", ytErr: err?.message || "YouTube share failed." } }));
    }
  }, []);
  const resolvedSessionId = sessionIdProp || lastEndedSessionId || activeSessionId || null;
  const { species: speciesCatalog } = useAnglerCatalog();
  const [sessionDoc, setSessionDoc] = React.useState(null);
  const [catches, setCatches] = React.useState([]);
  const [conditionTrackPoints, setConditionTrackPoints] = React.useState([]);
  const [conditionSeries, setConditionSeries] = React.useState([]);
  const [showCatchLog, setShowCatchLog] = React.useState(true);
  // Trip-report catch filter: "all" | "landed" | "missed". Missed fish (the
  // one that got away) can be hidden from the map + catch log via this control.
  const [catchFilter, setCatchFilter] = React.useState("all");
  const [showSessionAnalysis, setShowSessionAnalysis] = React.useState(true);
  const [selectedCatchId, setSelectedCatchId] = React.useState(null);
  const catchRowRefs = React.useRef({});

  // ── Trip notes (angler-authored, persisted to the session doc) ──────────
  const [notesDraft, setNotesDraft] = React.useState("");
  const [showNotes, setShowNotes] = React.useState(true);
  const [notesStatus, setNotesStatus] = React.useState("idle"); // idle|saving|saved|error
  const [notesError, setNotesError] = React.useState(null);
  const notesInitRef = React.useRef(null);

  React.useEffect(() => {
    // Allow the notes draft to re-seed when switching sessions.
    notesInitRef.current = null;
  }, [resolvedSessionId]);

  React.useEffect(() => {
    if (sessionDoc && notesInitRef.current !== resolvedSessionId) {
      notesInitRef.current = resolvedSessionId;
      setNotesDraft(typeof sessionDoc.userNotes === "string" ? sessionDoc.userNotes : "");
      setNotesStatus("idle");
      setNotesError(null);
    }
  }, [sessionDoc, resolvedSessionId]);

  const savedNotes = typeof sessionDoc?.userNotes === "string" ? sessionDoc.userNotes : "";
  const notesDirty = notesDraft.trim() !== savedNotes.trim();

  const saveNotes = React.useCallback(async () => {
    if (!userId || !resolvedSessionId) return;
    setNotesStatus("saving");
    setNotesError(null);
    try {
      const ref = doc(db, "users", userId, "sessions", resolvedSessionId);
      await authedWrite(
        () => updateDoc(ref, { userNotes: notesDraft.trim(), userNotesUpdatedAt: serverTimestamp() }),
        "save trip notes"
      );
      setNotesStatus("saved");
    } catch (err) {
      console.error("[Report] failed to save trip notes:", err);
      setNotesError("Couldn't save your notes. Please try again.");
      setNotesStatus("error");
    }
  }, [userId, resolvedSessionId, notesDraft]);

  // ── AI Session Analysis (Pro-only, LLM-generated) ──────────────────────
  const isPro = isProActive(userProfile);
  const [analysis, setAnalysis] = React.useState(null);
  const [analysisState, setAnalysisState] = React.useState("idle"); // idle|loading|ready|error|locked
  const [analysisError, setAnalysisError] = React.useState(null);
  const [analysisReloadKey, setAnalysisReloadKey] = React.useState(0);

  React.useEffect(() => {
    if (!userId || !resolvedSessionId) {
      setSessionDoc(null);
      setCatches([]);
      return;
    }

    const sessionRef = doc(db, "users", userId, "sessions", resolvedSessionId);
    const catchesRef = collection(db, "users", userId, "sessions", resolvedSessionId, "catches");

    const unsubSession = onSnapshot(
      sessionRef,
      (sessionSnap) => {
        setSessionDoc(sessionSnap.exists() ? sessionSnap.data() : null);
      },
      (error) => {
        console.error("[Report] failed to load session report:", error);
        setSessionDoc(null);
      }
    );

    const unsubCatches = onSnapshot(
      catchesRef,
      async (catchesSnap) => {
        const catchPromises = catchesSnap.docs.map(async (item) => {
          const data = item.data() || {};
          const caughtAtDate = data.caughtAt?.toDate?.() || null;
          const tide = data.conditionsSnapshot?.tideStage || "Tide —";
          const baro = formatBaro(data.conditionsSnapshot?.barometric);
          const certified = Boolean(data.videoCertified || data.certified || data.certificateId);
          const certificateId = data.certificateId || null;
          
          let videoDownloadUrl = null;
          let videoStoragePath = null;
          let videoGps = null;
          let existingYoutubeUrl = "";
          let existingFacebookPermalink = "";
          let existingViewCount = 0;
          if (certificateId) {
            try {
              const certSnap = await getDoc(doc(db, "certificates", certificateId));
              if (certSnap.exists()) {
                const certData = certSnap.data() || {};
                videoDownloadUrl = certData.downloadUrl || null;
                videoStoragePath = certData.storagePath || null;
                if (typeof certData?.gps?.lat === "number" && typeof certData?.gps?.lng === "number") {
                  videoGps = { lat: certData.gps.lat, lng: certData.gps.lng };
                }
              }
              const videoSnap = await getDoc(doc(db, "videos", certificateId));
              if (videoSnap.exists()) {
                const vd = videoSnap.data() || {};
                existingYoutubeUrl = vd.youtubeUrl || vd.youtubeVideoUrl || "";
                existingFacebookPermalink = vd?.facebookUpload?.permalinkUrl || "";
                existingViewCount = Number(vd.viewCount || 0);
              }
            } catch (error) {
              console.warn("[Report] failed to load video for cert:", certificateId, error);
            }
          }

          // On R2 the durable downloadUrl is replaced by an on-demand presigned
          // GET minted from the storagePath; on Firebase this returns the
          // stored downloadUrl unchanged.
          const resolvedVideoUrl = await resolveVideoUrl({
            storagePath: videoStoragePath,
            downloadUrl: videoDownloadUrl,
          });

          return {
            id: item.id,
            species: data.species || "Unknown",
            outcome: data.outcome === "missed" ? "missed" : "landed",
            size: formatLength(data.lengthIn),
            weight: formatWeight(data.weightLbs),
            weightLbs: Number.isFinite(Number(data.weightLbs)) ? Number(data.weightLbs) : null,
            lengthIn: Number.isFinite(Number(data.lengthIn)) ? Number(data.lengthIn) : null,
            lure: data.lure || "—",
            waterClarity: data.waterClarity || "—",
            time: caughtAtDate
              ? new Intl.DateTimeFormat(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                }).format(caughtAtDate)
              : "—",
            cond: `${tide} · ${baro}`,
            certified,
            certificateId,
            youtubeUrl: existingYoutubeUrl,
            facebookPermalink: existingFacebookPermalink,
            viewCount: existingViewCount,
            caughtAt: data.caughtAt || null,
            conditionsSnapshot: data.conditionsSnapshot || null,
            videoDownloadUrl: resolvedVideoUrl,
            videoGps,
            catchGps:
              typeof data?.location?.lat === "number" && typeof data?.location?.lng === "number"
                ? { lat: data.location.lat, lng: data.location.lng }
                : null,
          };
        });

        const resolved = await Promise.all(catchPromises);
        // Number catches chronologically (earliest caught = #1) so the map,
        // catch log, and Conditions Timeline all read consistently. Display
        // order stays newest-first.
        const chronological = [...resolved].sort((a, b) => toMs(a.caughtAt) - toMs(b.caughtAt));
        const seqById = new Map(chronological.map((item, index) => [item.id, index + 1]));
        const next = resolved
          .sort((a, b) => toMs(b.caughtAt) - toMs(a.caughtAt))
          .map((item) => ({ ...item, sequence: seqById.get(item.id) }));
        setCatches(next);
        // Seed the per-cert share state from already-persisted share results so
        // clips posted earlier show as "Posted" without re-posting.
        setShareState((prev) => {
          const seeded = { ...prev };
          for (const c of next) {
            if (!c.certificateId) continue;
            const cur = seeded[c.certificateId] || {};
            seeded[c.certificateId] = {
              fb: cur.fb || (c.facebookPermalink ? "confirmed" : "idle"),
              fbUrl: cur.fbUrl || c.facebookPermalink || "",
              fbErr: cur.fbErr || "",
              yt: cur.yt || (c.youtubeUrl ? "confirmed" : "idle"),
              ytUrl: cur.ytUrl || c.youtubeUrl || "",
              ytErr: cur.ytErr || "",
            };
          }
          return seeded;
        });
      },
      (error) => {
        console.error("[Report] failed to load catches:", error);
        setCatches([]);
      }
    );

    return () => {
      unsubSession();
      unsubCatches();
    };
  }, [resolvedSessionId, userId]);

  React.useEffect(() => {
    if (!userId || !resolvedSessionId) {
      setConditionTrackPoints([]);
      return;
    }

    const conditionLogRef = collection(db, "users", userId, "sessions", resolvedSessionId, "conditionLog");
    const unsubConditionLog = onSnapshot(
      conditionLogRef,
      (snap) => {
        const track = [];
        const seriesPoints = [];
        snap.docs.forEach((item) => {
          const data = item.data() || {};
          const at = typeof data.clientCapturedAtMs === "number" ? data.clientCapturedAtMs : toMs(data.capturedAt);
          if (typeof data?.location?.lat === "number" && typeof data?.location?.lng === "number") {
            track.push({ lat: data.location.lat, lng: data.location.lng, at });
          }
          const conditions = data.conditions || null;
          if (conditions && Number.isFinite(at)) {
            seriesPoints.push({
              atMs: at,
              baro: Number(conditions?.barometric?.inHg),
              wind: Number(conditions?.wind?.speedKnots),
              tideStage: conditions?.tideStage || null,
              solunar: conditions?.solunarMajor || null,
            });
          }
        });
        track.sort((a, b) => a.at - b.at);
        seriesPoints.sort((a, b) => a.atMs - b.atMs);
        setConditionTrackPoints(track);
        setConditionSeries(seriesPoints);
      },
      (error) => {
        console.error("[Report] failed to load condition log:", error);
        setConditionTrackPoints([]);
        setConditionSeries([]);
      }
    );

    return () => unsubConditionLog();
  }, [resolvedSessionId, userId]);

  const catchCount = catches.filter((item) => item.outcome !== "missed").length;
  const missedCount = catches.filter((item) => item.outcome === "missed").length;
  // Catches shown in the map + log after applying the outcome filter.
  const filteredCatches = React.useMemo(
    () =>
      catches.filter((item) => {
        if (catchFilter === "landed") return item.outcome !== "missed";
        if (catchFilter === "missed") return item.outcome === "missed";
        return true;
      }),
    [catches, catchFilter]
  );
  const certifiedCount = catches.filter((item) => item.certified).length;
  const hoursLabel = formatHours(sessionDoc?.startedAt, sessionDoc?.endedAt);

  // Prep Score snapshot captured when the trip started (SessionContext.startSession).
  // Older sessions predate the snapshot and have no score — show a dash there.
  const prepScoreValue = Number(sessionDoc?.prepScore?.score);
  const hasPrepScore = Number.isFinite(prepScoreValue) && prepScoreValue > 0;
  const prepConfidenceRaw = String(sessionDoc?.prepScore?.confidence || "").toLowerCase();
  const prepConfidenceLabel =
    prepConfidenceRaw === "high"
      ? "HIGH"
      : prepConfidenceRaw === "medium"
      ? "MED"
      : prepConfidenceRaw === "low"
      ? "LOW"
      : "";

  // ── "Did the prediction pay off?" outcome heuristic ──────────────────────
  // A single fish counts as a "good catch" when it's verified, or clears that
  // species' quality threshold from the angler catalog (goodWeightLbs /
  // goodLengthIn). Species without a catalog baseline fall back to a generic
  // bar. A "good day" is then a productive session overall.
  const GENERIC_GOOD_WEIGHT_LBS = 5;    // fallback when species has no baseline
  const GENERIC_GOOD_LENGTH_IN = 24;    // fallback when species has no baseline
  const GOOD_DAY_MIN_FISH = 3;          // multiple fish = action, not a fluke
  const GOOD_DAY_MIN_RATE_PER_HOUR = 1; // steady bite, normalized for trip length
  const PREP_HIGH_MIN = 70;             // Prep Score considered "high/favorable"

  const isGoodCatch = React.useCallback(
    (item) => {
      if (item.certified) return true;
      const baseline = findSpeciesBaseline(speciesCatalog, item.species);
      const weightBar = Number.isFinite(baseline?.goodWeightLbs)
        ? baseline.goodWeightLbs
        : GENERIC_GOOD_WEIGHT_LBS;
      const lengthBar = Number.isFinite(baseline?.goodLengthIn)
        ? baseline.goodLengthIn
        : GENERIC_GOOD_LENGTH_IN;
      return (
        (Number.isFinite(item.weightLbs) && item.weightLbs >= weightBar) ||
        (Number.isFinite(item.lengthIn) && item.lengthIn >= lengthBar)
      );
    },
    [speciesCatalog]
  );

  const goodCatchCount = catches.filter(isGoodCatch).length;

  const durationHours = Math.max(0, toMs(sessionDoc?.endedAt) - toMs(sessionDoc?.startedAt)) / 3600000;
  const catchRatePerHour = durationHours > 0 ? catchCount / durationHours : 0;
  const wasGoodDay =
    goodCatchCount > 0 ||
    catchCount >= GOOD_DAY_MIN_FISH ||
    catchRatePerHour >= GOOD_DAY_MIN_RATE_PER_HOUR;

  // The prediction paid off: a high Prep Score that lined up with a good day.
  const prepWasAccurate = hasPrepScore && prepScoreValue >= PREP_HIGH_MIN && wasGoodDay;
  const trackedMiles = React.useMemo(() => {
    const points = [];

    if (typeof sessionDoc?.location?.lat === "number" && typeof sessionDoc?.location?.lng === "number") {
      points.push({
        lat: sessionDoc.location.lat,
        lng: sessionDoc.location.lng,
        at: toMs(sessionDoc?.startedAt),
      });
    }

    conditionTrackPoints.forEach((point) => points.push(point));
    catches.forEach((item) => {
      if (item.catchGps && typeof item.catchGps.lat === "number" && typeof item.catchGps.lng === "number") {
        points.push({ lat: item.catchGps.lat, lng: item.catchGps.lng, at: toMs(item.caughtAt) });
      }
    });

    const ordered = points
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
      .sort((a, b) => a.at - b.at);

    let total = 0;
    for (let index = 1; index < ordered.length; index += 1) {
      const previous = ordered[index - 1];
      const current = ordered[index];
      if (previous.lat === current.lat && previous.lng === current.lng) continue;
      total += haversineMiles(previous, current);
    }

    return total;
  }, [catches, conditionTrackPoints, sessionDoc]);
  const mappedCatches = React.useMemo(
    () =>
      filteredCatches.filter(
        (item) =>
          (item.videoGps && typeof item.videoGps.lat === "number" && typeof item.videoGps.lng === "number") ||
          (item.catchGps && typeof item.catchGps.lat === "number" && typeof item.catchGps.lng === "number")
      ),
    [filteredCatches]
  );

  // Conditions Timeline data: merge the session-start snapshot, the 15-min
  // condition log, and each catch's own snapshot into one time-ordered series.
  const conditionChartSeries = React.useMemo(() => {
    const pts = [];
    const startConds = sessionDoc?.conditions;
    const startMs = toMs(sessionDoc?.startedAt);
    if (startConds && Number.isFinite(startMs)) {
      pts.push({
        atMs: startMs,
        baro: Number(startConds?.barometric?.inHg),
        wind: Number(startConds?.wind?.speedKnots),
        tideStage: startConds?.tideStage || null,
        solunar: startConds?.solunarMajor || null,
      });
    }
    conditionSeries.forEach((p) => pts.push(p));
    catches.forEach((c) => {
      const cs = c.conditionsSnapshot;
      const atMs = toMs(c.caughtAt);
      if (cs && Number.isFinite(atMs)) {
        pts.push({
          atMs,
          baro: Number(cs?.barometric?.inHg),
          wind: Number(cs?.wind?.speedKnots),
          tideStage: cs?.tideStage || null,
          solunar: cs?.solunarMajor || null,
        });
      }
    });
    return pts.sort((a, b) => a.atMs - b.atMs);
  }, [sessionDoc, conditionSeries, catches]);

  const conditionChartCatches = React.useMemo(
    () =>
      catches
        .filter((c) => c.outcome !== "missed")
        .map((c) => {
          const cs = c.conditionsSnapshot;
          return {
            id: c.id,
            sequence: c.sequence,
            color: getSequenceColor(c),
            title: `#${c.sequence} · ${c.species}${c.time && c.time !== "—" ? ` · ${c.time}` : ""}`,
            atMs: toMs(c.caughtAt),
            baro: Number(cs?.barometric?.inHg),
            wind: Number(cs?.wind?.speedKnots),
            tideStage: cs?.tideStage || null,
          };
        })
        .filter((c) => Number.isFinite(c.atMs))
        .sort((a, b) => a.sequence - b.sequence),
    [catches]
  );

  const mapCenter = React.useMemo(() => {
    if (!mappedCatches.length) return [29.5, -88.5];
    const centerPoint = mappedCatches[0].videoGps || mappedCatches[0].catchGps;
    return [centerPoint.lat, centerPoint.lng];
  }, [mappedCatches]);
  const selectedCatch = React.useMemo(() => {
    const byId = catches.find((item) => item.id === selectedCatchId);
    if (byId) return byId;
    const withVideo = catches.find((item) => !!item.videoDownloadUrl);
    return withVideo || catches[0] || null;
  }, [catches, selectedCatchId]);

  React.useEffect(() => {
    if (!selectedCatchId && catches.length > 0) {
      const withVideo = catches.find((item) => !!item.videoDownloadUrl);
      setSelectedCatchId((withVideo || catches[0]).id);
      return;
    }

    if (selectedCatchId && !catches.some((item) => item.id === selectedCatchId)) {
      const withVideo = catches.find((item) => !!item.videoDownloadUrl);
      setSelectedCatchId((withVideo || catches[0] || null)?.id || null);
    }
  }, [catches, selectedCatchId]);

  // Deep-link from the trip-recap email: open the catch log, select the
  // requested catch, and scroll its row into view so the angler lands on the
  // exact video they tapped "Watch" for.
  React.useEffect(() => {
    if (!highlightCatchId) return;
    if (!catches.some((item) => item.id === highlightCatchId)) return;
    setShowCatchLog(true);
    setSelectedCatchId(highlightCatchId);
    const node = catchRowRefs.current[highlightCatchId];
    if (node?.scrollIntoView) {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        node.scrollIntoView();
      }
    }
    onHighlightConsumed?.();
  }, [highlightCatchId, catches, onHighlightConsumed]);

  // Fetch the LLM-generated session recap. Pro-only — free users see an
  // upsell card and we never hit the API. The server re-checks entitlement
  // and reads the session + catches from Firestore itself, so we only need
  // userId + sessionId. Results are cached per session in aiClient.js.
  //
  // First preference is the recap persisted on the session doc itself
  // (sessionDoc.aiAnalysis), written by the backend when it was first
  // generated. That makes a past session's analysis viewable instantly on
  // ANY device and while offline, with no token re-spend. We only call the
  // API when nothing is stored yet (or the user taps regenerate).
  React.useEffect(() => {
    if (!userId || !resolvedSessionId) {
      setAnalysisState("idle");
      setAnalysis(null);
      return;
    }
    if (!isPro) {
      setAnalysisState("locked");
      setAnalysis(null);
      return;
    }

    // Use the persisted recap from the session doc when present, unless the
    // user explicitly asked to regenerate.
    const storedAnalysis = sessionDoc?.aiAnalysis;
    if (
      analysisReloadKey === 0 &&
      storedAnalysis?.status === "ready" &&
      storedAnalysis?.result
    ) {
      setAnalysis(storedAnalysis.result);
      setAnalysisState("ready");
      setAnalysisError(null);
      return;
    }

    let cancelled = false;
    setAnalysisState("loading");
    setAnalysisError(null);

    requestSessionAnalysis({
      userId,
      sessionId: resolvedSessionId,
      stationName: sessionDoc?.tideStationName || null,
      forceRegenerate: analysisReloadKey > 0,
    })
      .then((data) => {
        if (cancelled) return;
        const result = data?.result || null;
        if (result) {
          setAnalysis(result);
          setAnalysisState("ready");
        } else {
          setAnalysisState("error");
          setAnalysisError("No analysis returned.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ProRequiredError) {
          setAnalysisState("locked");
          return;
        }
        setAnalysisState("error");
        setAnalysisError(err?.message || "Could not generate analysis.");
      });

    return () => {
      cancelled = true;
    };
    // sessionDoc?.tideStationName is read but intentionally omitted to avoid
    // refetching when the snapshot re-fires with identical station data.
    // sessionDoc?.aiAnalysis is handled via the early-return above; we key the
    // effect on its presence so a freshly-persisted recap surfaces immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, resolvedSessionId, isPro, analysisReloadKey, sessionDoc?.aiAnalysis]);

  return (
    <div className="hide-scrollbar" style={{ flex: 1, overflowY: "auto", paddingBottom: 70 }}>
      <div style={{ padding: "18px 18px 8px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>TRIP REPORT</div>
          <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
            {formatDateHeading(sessionDoc?.startedAt)} · Session
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{hoursLabel} · {catchCount} fish · {formatMiles(trackedMiles)}</div>
        </div>
        <ProfileAvatar
          url={getProfilePhotoUrl(userProfile)}
          name={[userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(" ")}
          size={34}
          title="You"
          style={{ marginTop: 2, marginRight: 36 }}
        />
      </div>
      <div
        style={{
          margin: "8px 14px 0",
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${C.border}`,
          height: 172,
          position: "relative",
          background: "#06192e",
        }}
      >
        {mappedCatches.length > 0 ? (
          <MapContainer
            center={mapCenter}
            zoom={13}
            zoomControl
            attributionControl={false}
            dragging
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <TileLayer url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            {mappedCatches.map((item) => (
              <Marker
                key={item.id}
                position={[(item.videoGps || item.catchGps).lat, (item.videoGps || item.catchGps).lng]}
                icon={buildSequenceIcon(item.sequence, getSequenceColor(item), item.outcome === "missed")}
              />
            ))}
          </MapContainer>
        ) : (
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,#06192e,#0a2440)" }} />
        )}
        <div style={{ position: "absolute", bottom: 8, left: 12, fontSize: 9, color: C.muted }}>
          Numbered catch points
        </div>
        {mappedCatches.length === 0 && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 11, color: C.muted }}>
            No video GPS points available
          </div>
        )}
      </div>
      <div style={{ margin: "10px 14px" }}>
        <CollapsibleSection
          glow={C.accent}
          style={{ background: `${C.accent}08` }}
          open={showSessionAnalysis}
          onToggle={() => setShowSessionAnalysis((v) => !v)}
          headerTitle={showSessionAnalysis ? "Hide AI session analysis" : "Show AI session analysis"}
          onRefresh={analysisState === "ready" ? () => setAnalysisReloadKey((k) => k + 1) : null}
          refreshTitle="Regenerate analysis"
          headerRight={analysisState === "locked" ? (
              <span
                id="report-session-analysis-locked"
                data-testid="report-session-analysis-locked"
                style={{
                  flexShrink: 0,
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: 1.5,
                  color: "#1A0E04",
                  background: C.warning,
                  border: `1px solid ${C.warning}`,
                  borderRadius: 6,
                  padding: "3px 7px",
                  boxShadow: `0 1px 6px ${C.warning}66`,
                }}
              >
                PRO FEATURE
              </span>
            ) : null}
          bodyStyle={{ padding: "0 14px 12px" }}
          title={<span style={{ fontSize: 9, color: C.accent, letterSpacing: 2 }}>AI SESSION ANALYSIS</span>}
        >

          {analysisState === "loading" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  border: `2px solid ${C.accent}55`,
                  borderTopColor: C.accent,
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <span style={{ fontSize: 11, color: C.muted }}>Analyzing your session…</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {analysisState === "locked" && (
            <div>
              <div style={{ fontSize: 12, color: C.sand, lineHeight: 1.6 }}>
                Unlock an AI-written recap of every trip — the tide windows, lures, and
                patterns that produced your fish — with <strong style={{ color: C.gold }}>Pro</strong>.
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>
                Available to Pro Anglers.
              </div>
            </div>
          )}

          {analysisState === "error" && (
            <div>
              <div style={{ fontSize: 12, color: C.sand, lineHeight: 1.6 }}>
                {analysisError || "Could not generate analysis right now."}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAnalysisReloadKey((k) => k + 1)}
                style={{ marginTop: 8 }}
              >
                Try again
              </Button>
            </div>
          )}

          {analysisState === "ready" && analysis && (
            <div>
              <div style={{ fontSize: 13, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700, lineHeight: 1.5, marginBottom: 6 }}>
                {analysis.headline}
              </div>
              <div style={{ fontSize: 12, color: C.sand, fontFamily: "Georgia,serif", fontStyle: "italic", lineHeight: 1.6 }}>
                {analysis.summary}
              </div>
              {Array.isArray(analysis.bullets) && analysis.bullets.length > 0 && (
                <ul style={{ margin: "8px 0 0", padding: "0 0 0 16px" }}>
                  {analysis.bullets.map((b, i) => (
                    <li key={i} style={{ fontSize: 11, color: C.sand, lineHeight: 1.6, marginBottom: 2 }}>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(analysis.recommendations) && analysis.recommendations.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 8, color: C.teal, letterSpacing: 1.5, marginBottom: 3 }}>NEXT TIME</div>
                  {analysis.recommendations.map((r, i) => (
                    <div key={i} style={{ fontSize: 11, color: C.sand, lineHeight: 1.6, display: "flex", gap: 6 }}>
                      <span style={{ color: C.accent }}>›</span>
                      <span>{r}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CollapsibleSection>
      </div>
      <div style={{ margin: "0 14px 10px", display: "flex", gap: 8 }}>
        {[
          [String(catchCount), "FISH"],
          [String(certifiedCount), "CERTIFIED"],
          [hoursLabel.replace("h ", ":").replace("m", ""), "HOURS"],
          [hasPrepScore ? String(Math.round(prepScoreValue)) : "—", "PREP"],
        ].map(([v, l]) => {
          const isPrep = l === "PREP";
          const highlight = isPrep && prepWasAccurate;
          return (
            <div
              key={l}
              title={highlight ? "High Prep Score that paid off — favorable forecast and a productive day on the water." : undefined}
              style={{
                flex: 1,
                position: "relative",
                background: highlight ? `${C.seafoam}1f` : C.cardBg,
                borderRadius: 8,
                padding: "10px 6px",
                textAlign: "center",
                border: `1px solid ${highlight ? C.seafoam : C.border}`,
                boxShadow: highlight ? `0 0 12px ${C.seafoam}66` : "none",
              }}
            >
              {highlight && (
                <div
                  style={{
                    position: "absolute",
                    top: -7,
                    right: -6,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: C.seafoam,
                    color: "#06101e",
                    fontSize: 10,
                    fontWeight: 900,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 0 8px ${C.seafoam}aa`,
                  }}
                  aria-label="Prep Score prediction paid off"
                >
                  ✓
                </div>
              )}
              <div style={{ fontSize: 18, fontWeight: 800, color: l === "CERTIFIED" ? C.gold : highlight ? C.seafoam : C.accent, fontFamily: "Georgia,serif" }}>
                {v}
              </div>
              <div style={{ fontSize: 7, color: C.muted, letterSpacing: 1 }}>
                {isPrep && hasPrepScore && prepConfidenceLabel ? `${l} · ${prepConfidenceLabel}` : l}
              </div>
            </div>
          );
        })}
      </div>
      <SessionConditionsChart
        series={conditionChartSeries}
        catches={conditionChartCatches}
        startMs={toMs(sessionDoc?.startedAt)}
        endMs={toMs(sessionDoc?.endedAt)}
      />
      <div style={{ margin: "0 14px 12px" }}>
        <Card style={{ padding: "14px" }}>
          <button
            type="button"
            onClick={() => setShowNotes((v) => !v)}
            title={showNotes ? "Hide notes" : "Show notes"}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: showNotes ? 10 : 0, textAlign: "left" }}
          >
            <span style={{ flex: 1, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
              MY NOTES
              {savedNotes.trim() && !showNotes && <span style={{ letterSpacing: 0 }}> · saved</span>}
            </span>
            <span style={{ color: C.muted, fontSize: 13, transform: showNotes ? "none" : "rotate(-90deg)", transition: "transform .2s" }}>▾</span>
          </button>
          {showNotes && (
          <>
          <textarea
            value={notesDraft}
            onChange={(e) => {
              setNotesDraft(e.target.value);
              if (notesStatus !== "idle") setNotesStatus("idle");
            }}
            placeholder="Add your own notes about this trip — what worked, conditions you noticed, spots to remember…"
            rows={4}
            style={{
              width: "100%",
              boxSizing: "border-box",
              resize: "vertical",
              minHeight: 90,
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: C.deepWater,
              color: C.white,
              fontSize: 13,
              lineHeight: 1.5,
              fontFamily: "inherit",
              outline: "none",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
            <Button
              onClick={saveNotes}
              disabled={!notesDirty || notesStatus === "saving"}
              size="sm"
            >
              {notesStatus === "saving" ? "Saving…" : "Save Notes"}
            </Button>
            {notesStatus === "saved" && !notesDirty && (
              <span style={{ fontSize: 11, color: C.seafoam }}>Saved ✓</span>
            )}
            {notesStatus === "error" && (
              <span style={{ fontSize: 11, color: C.warning }}>{notesError}</span>
            )}
          </div>
          </>
          )}
        </Card>
      </div>
      <div style={{ margin: "0 14px 10px" }}>
        <CollapsibleSection
          open={showCatchLog}
          onToggle={() => setShowCatchLog(!showCatchLog)}
          headerTitle={showCatchLog ? "Hide catch log" : "Show catch log"}
          title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>CATCH LOG</span>}
        >
        {missedCount > 0 && (
          <div
            role="group"
            aria-label="Filter catches by outcome"
            style={{ display: "flex", gap: 6, marginBottom: 8 }}
          >
            {[
              ["all", `All (${catchCount + missedCount})`],
              ["landed", `Landed (${catchCount})`],
              ["missed", `Missed (${missedCount})`],
            ].map(([value, label]) => {
              const active = catchFilter === value;
              const activeColor = value === "missed" ? C.warning : C.seafoam;
              return (
                <button
                  key={value}
                  type="button"
                  data-testid={`report-catch-filter-${value}`}
                  data-selected={active ? "true" : "false"}
                  onClick={() => setCatchFilter(value)}
                  style={{
                    flex: 1,
                    padding: "6px 0",
                    borderRadius: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    cursor: "pointer",
                    border: `1px solid ${active ? activeColor : C.border}`,
                    background: active ? `${activeColor}22` : "transparent",
                    color: active ? activeColor : C.muted,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        <div className="hide-scrollbar" style={{ maxHeight: 260, overflowY: "auto", paddingRight: 2 }}>
          {filteredCatches.map((c) => (
            <button
              key={c.id}
              type="button"
              ref={(node) => {
                if (node) catchRowRefs.current[c.id] = node;
                else delete catchRowRefs.current[c.id];
              }}
              onClick={() => setSelectedCatchId(c.id)}
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
              }}
            >
            <Card
              style={{
                padding: "10px 12px",
                marginBottom: 6,
                borderColor: selectedCatchId === c.id ? C.accent + "77" : c.certified ? C.gold + "44" : "#1e3448",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                {c.videoDownloadUrl ? (
                  <VideoThumbnail src={c.videoDownloadUrl} />
                ) : (
                  <div style={{
                    width: 72,
                    height: 54,
                    borderRadius: 7,
                    background: "#0a1929",
                    border: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <span style={{ fontSize: 18, opacity: 0.4 }}>🎬</span>
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif" }}>
                    <SequenceBadge sequence={c.sequence} color={getSequenceColor(c, { row: true })} />
                    <span>
                      {c.species}{" "}
                      {c.outcome === "missed" ? null : (
                        <span style={{ color: C.gold, fontSize: 12 }}>{c.size}</span>
                      )}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                    {c.outcome === "missed"
                      ? `${c.lure} · Clarity: ${c.waterClarity}`
                      : `${c.weight} · ${c.lure} · Clarity: ${c.waterClarity}`}
                  </div>
                  <div style={{ fontSize: 9, color: C.teal, marginTop: 2 }}>{c.cond}</div>
                  {!c.videoGps && !c.catchGps && (
                    <div style={{ fontSize: 9, color: C.red, marginTop: 3 }}>
                      GPS missing for this catch
                    </div>
                  )}
                </div>

                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, color: C.muted }}>{c.time}</div>
                  {c.outcome === "missed" ? (
                    <Badge color={C.warning} style={{ marginTop: 4, fontSize: 8 }}>
                      MISSED
                    </Badge>
                  ) : c.certified ? (
                    <Badge color={C.gold} style={{ marginTop: 4, fontSize: 8 }}>
                      🎥 CERTIFIED
                    </Badge>
                  ) : null}
                  {c.certified && (
                    <div style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>👁 {c.viewCount || 0}</div>
                  )}
                </div>
              </div>
              {shareVideos && c.certified && c.certificateId && (() => {
                const st = shareState[c.certificateId] || {};
                const openUrl = (url) => {
                  try { window.open(url, "_blank", "noopener,noreferrer"); } catch {}
                };
                const chip = (label, doPost, state, url, color) =>
                  state === "confirmed" ? (
                    <span
                      key={label}
                      onClick={(e) => { e.stopPropagation(); if (url) openUrl(url); }}
                      style={{ fontSize: 10, fontWeight: 700, color: C.seafoam, cursor: url ? "pointer" : "default" }}
                    >
                      ✓ {label}{url ? " ↗" : ""}
                    </span>
                  ) : (
                    <span
                      key={label}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); if (state !== "posting") doPost(); }}
                      style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: color, borderRadius: 8, padding: "6px 10px", cursor: state === "posting" ? "default" : "pointer", opacity: state === "posting" ? 0.8 : 1 }}
                    >
                      {state === "posting" ? "Posting\u2026" : `Post to ${label}`}
                    </span>
                  );
                return (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
                  >
                    <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1, width: "100%" }}>SHARE THIS CATCH</span>
                    {chip("Facebook", () => handleShareFacebook(c), st.fb || (c.facebookPermalink ? "confirmed" : "idle"), st.fbUrl || c.facebookPermalink, "#1877F2")}
                    {chip("YouTube", () => handleShareYouTube(c), st.yt || (c.youtubeUrl ? "confirmed" : "idle"), st.ytUrl || c.youtubeUrl, "#c40000")}
                    {(st.fbErr || st.ytErr) && (
                      <span style={{ fontSize: 9, color: C.warning, width: "100%" }}>{st.fbErr || st.ytErr}</span>
                    )}
                  </div>
                );
              })()}
            </Card>
            </button>
          ))}
          {filteredCatches.length === 0 && (
            <Card style={{ padding: "10px 12px", marginBottom: 6 }}>
              <div style={{ fontSize: 11, color: C.muted }}>
                {catchFilter === "missed"
                  ? "No missed fish logged for this session."
                  : catchFilter === "landed"
                  ? "No landed catches for this session."
                  : "No catches logged for this session."}
              </div>
            </Card>
          )}
        </div>
        </CollapsibleSection>
      </div>
      <div style={{ margin: "0 14px 10px" }}>
        <div style={{ fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
          VIDEO VIEWER{selectedCatch?.certified ? ` · 👁 ${selectedCatch.viewCount || 0}` : ""}
        </div>
        <Card style={{ padding: "10px 12px" }}>
          <div
            style={{
              borderRadius: 10,
              overflow: "hidden",
              border: `1px solid ${C.border}`,
              background: "#0a1929",
              height: 190,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {selectedCatch?.videoDownloadUrl ? (
              <video
                src={selectedCatch.videoDownloadUrl}
                controls
                preload="metadata"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div style={{ fontSize: 11, color: C.muted }}>No video available for selected catch.</div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {[
              ["SPECIES", selectedCatch?.species || "—"],
              ["WEIGHT", selectedCatch?.weight || "—"],
              ["LENGTH", selectedCatch?.size || "—"],
              ["CLARITY", selectedCatch?.waterClarity || "—"],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  flex: 1,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: "8px 6px",
                  textAlign: "center",
                  background: C.cardBg,
                }}
              >
                <div style={{ fontSize: 8, color: C.muted, letterSpacing: 1.3 }}>{label}</div>
                <div style={{ fontSize: 11, color: C.white, fontWeight: 700, marginTop: 3 }}>{value}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
